import { sessionLifecycle, setSessionLifecycle } from "@main/state/session-lifecycle";
import { dismissToastByNotificationId } from "@main/state/toast-store";
import type { MarPoint, SessionDebugMeta, SessionRecord, SettingsChange } from "@main/types/db-types";
import { type Accessor, createSignal } from "solid-js";
import { resetRealtimeSignals } from "./realtime-detection-signals";

/**
 * §3.4 / §UC-09 / §UC-10 / §3.12: 実行中セッションの蓄積状態と永続化 record の組み立て。
 *
 * lifecycle (state machine: idle/calibrating/starting/running/completed + `requestSessionStart`) は
 * cross-feature な調整状態なので `@main/state/session-lifecycle` に分離している (§5.2)。 本モジュールは
 * その lifecycle を読みつつ、 「running 中だけ DB 永続化対象として残す」 という session feature 固有の
 * 蓄積責務に専念する。
 *
 * 30fps で更新される realtime signal (mar / isSpeaking / fps) は `realtime-detection-signals.ts` に
 * 隔離する (§5.2 / issue #18)。 この モジュールは「通知 / mute + 永続化用累積 (marPoints /
 * speakingIntervals / muteIntervals / settingsChanges)」 のみを扱う。
 *
 * issue #130: audio VAD 撤去で audioLevel / vadEngine / vadScore / vadStateNorm を AccumulatorMetrics
 * / MarPoint / SessionDebugMeta から削除。 isSpeaking は vision-VAD 由来の signal を accumulate する。
 *
 * frame の onResult では 「段 1: realtime signal 更新 (新モジュール)」 → 「段 2:
 * lifecycle ガード付き永続化累積 push (本モジュールの `pushSessionAccumulators`)」 の
 * 順で 2 段呼び出しする。
 *
 * 永続化用の累積データは signal ではないミュータブルな配列で、 セッション終了時に
 * buildSessionRecord() でスナップショット化する。
 */

type NotificationEvent = {
  id: string;
  /** performance.now 基準 (detection-loop の frame timestamp)。 セッション相対 ms への正規化は
   * buildSessionRecord で行うため、 この signal を直接読む realtime 消費者は相対時刻として
   * 扱わないこと (issue #292)。 */
  timestamp: number;
  mar: number;
  /** §UC-12: ユーザーフィードバック。 'bad' = 誤検知、 'good' = 役に立った通知。
   * 排他にするため union で表現 (mark 関数で上書きするだけで自然に排他になる)。 */
  feedback?: "good" | "bad";
  snapshot?: Blob;
};

// §3.12: 誤検知 / Good マーク発生時の即座保存をトリガするための listener 注入口。
// 注入は session-page 側 (lifecycle 一致) からのみ。 settings-store からの
// pushSettingsChange と同じ書き方に揃える。
let immediateSaveListener: (() => void) | null = null;

export function setImmediateSaveListener(fn: (() => void) | null): void {
  immediateSaveListener = fn;
}

const [muted, setMuted] = createSignal(false);
const [startedAt, setStartedAt] = createSignal<number | null>(null);
const [endedAt, setEndedAt] = createSignal<number | null>(null);
const [notifications, setNotifications] = createSignal<NotificationEvent[]>([]);
// §UC-12 / §3.2: 直近通知の id (PiP / トーストのフィードバックボタン表示制御用)。
// session-store の global signal にしておくことで、 PiP Window の独立 Solid root からも
// 同じ signal インスタンスを subscribe できるようにする。
const [latestNotificationId, setLatestNotificationId] = createSignal<string | null>(null);

// 永続化用の累積 (signal にしない: 30fps push で再描画させない)
let sessionId: string | null = null;
// issue #292: 累積 push の timestamp は frame-loop と同じ performance.now 基準 (単調時計)。
// 永続化時は perfT0 (startSession 時点の performance.now) を引いてセッション相対 ms に正規化する。
// 旧実装は muteIntervals と開区間クローズだけ Date.now 経過基準で、 チャートに ms 軸を共有させると
// 嘘の位置に描かれていた (負の長さの区間も生成しえた)。
// 既知の限界: OS サスペンド中は performance.now が止まる環境があり、 壁時計の durationMs
// (Date.now 基準) と乖離しうる。 チャート/区間は「実際に観測していた時間軸」を表すと割り切る。
let perfT0 = 0;
// completeSession 時点の performance.now。 完了後の遅延 rebuild (late feedback flush) で
// 開区間のクローズ位置が動かないように capture する。
let perfEndedAt: number | null = null;
const marPoints: MarPoint[] = [];
let lastMarPushAt = 0; // 1 秒間引き用

/**
 * boolean 状態の立ち上がり/立ち下がりエッジを {start, end} 区間に畳む tracker。
 * §issue #18: edge 検出は本モジュール内で完結させ、 realtime-detection-signals の signal を
 * 読まない (= cross-module signal 依存を作らない)。 issue #292 で speaking / open / unobserved /
 * mute の 4 系列に増えたため共通化した。
 */
function createIntervalTracker() {
  const intervals: Array<{ start: number; end: number }> = [];
  let fromMs: number | null = null;
  let prevActive = false;
  return {
    update(active: boolean, timestamp: number): void {
      if (active && !prevActive) {
        fromMs = timestamp;
      } else if (!active && prevActive && fromMs !== null) {
        intervals.push({ start: fromMs, end: timestamp });
        fromMs = null;
      }
      prevActive = active;
    },
    /** まだ開いている区間を closeAt で閉じたスナップショットを返す (内部状態は変えない)。 */
    snapshotClosed(closeAt: number): Array<{ start: number; end: number }> {
      return fromMs !== null ? [...intervals, { start: fromMs, end: closeAt }] : [...intervals];
    },
    reset(): void {
      intervals.length = 0;
      fromMs = null;
      prevActive = false;
    },
  };
}

const speakingTracker = createIntervalTracker();
// issue #292: 開口継続区間。 detection-machine B 層の openSince 成立/リセットを isOpen の
// エッジとして受け取り、 speakingIntervals と同じ方式で区間化する。
const openTracker = createIntervalTracker();
// issue #292: 顔未検出区間 (= 非監視時間)。 active = !faceDetected。 tracker 初期状態は
// inactive (= 顔あり) なので、 セッション開始 1 frame 目から faceDetected=false ならその
// frame が立ち上がりエッジになり区間が開く。
const unobservedTracker = createIntervalTracker();
const muteTracker = createIntervalTracker();
const settingsChanges: SettingsChange[] = [];
// SessionDebugMeta は現状空 object (issue #130 で audio 系 field を撤去後、 vision-VAD meta は
// 未追加)。 setter が必要になった時点で再 export する (YAGNI)。
const debugMeta: SessionDebugMeta = {};
const MAR_INTERVAL_MS = 1_000;

export const sessionMuted: Accessor<boolean> = muted;
export const sessionStartedAt: Accessor<number | null> = startedAt;
export const sessionNotifications: Accessor<NotificationEvent[]> = notifications;
export const sessionLatestNotificationId: Accessor<string | null> = latestNotificationId;
export { setLatestNotificationId };

export function startSession(
  id: string = `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
): string {
  sessionId = id;
  perfT0 = performance.now();
  perfEndedAt = null;
  setSessionLifecycle("running");
  setStartedAt(Date.now());
  setEndedAt(null);
  setNotifications([]);
  setLatestNotificationId(null);
  setMuted(false);
  marPoints.length = 0;
  speakingTracker.reset();
  openTracker.reset();
  unobservedTracker.reset();
  muteTracker.reset();
  settingsChanges.length = 0;
  lastMarPushAt = 0;
  // セッション開始〜最初のフレームまで (= worker cold-init、 実測 ~4s) はまだ顔を観測できて
  // いないので unobserved として開いておく。 最初の frame が faceDetected=true なら閉じ、
  // false なら連続した 1 区間になる (PR #294 review fix: チャート x 軸 0 起点との整合)。
  unobservedTracker.update(true, perfT0);
  return id;
}

export function completeSession(): void {
  perfEndedAt = performance.now();
  setSessionLifecycle("completed");
  setEndedAt(Date.now());
}

export function setSessionMuted(next: boolean): void {
  setMuted(next);
  if (sessionLifecycle() !== "running") return;
  // issue #292: marPoints / speakingIntervals と同じ performance.now 基準で記録する
  // (正規化は buildSessionRecord に一元化)。
  muteTracker.update(next, performance.now());
}

export function pushNotification(event: NotificationEvent): void {
  setNotifications([...notifications(), event]);
}

export function markNotificationBad(id: string): void {
  setNotifications(notifications().map((n) => (n.id === id ? { ...n, feedback: "bad" } : n)));
  // §UC-12: フィードバックボタンを押下と同時に PiP / メイン画面の通知トーストを隠す
  // (タップしたのに何も起きないように見える対策)。 PiP 側からの呼び出しでもメイン画面の
  // トーストを dismiss するため、 ここで一元的に処理する。
  if (latestNotificationId() === id) setLatestNotificationId(null);
  dismissToastByNotificationId(id);
  // running または completed (停止直後でも record 上書きで feedback 反映) で save する。
  // teardown 後に listener が null に戻れば自然に no-op。
  if (sessionLifecycle() !== "idle") immediateSaveListener?.();
}

export function markNotificationGood(id: string): void {
  setNotifications(notifications().map((n) => (n.id === id ? { ...n, feedback: "good" } : n)));
  if (latestNotificationId() === id) setLatestNotificationId(null);
  dismissToastByNotificationId(id);
  if (sessionLifecycle() !== "idle") immediateSaveListener?.();
}

export function attachSnapshotToNotification(id: string, snapshot: Blob): void {
  setNotifications(notifications().map((n) => (n.id === id ? { ...n, snapshot } : n)));
}

/**
 * §3.12 / UC-20: セッション中の設定変更を記録する。 settings-store の listener から
 * 呼ばれる想定で、 lifecycle が running のときだけ追記する。 idle 時のホーム画面からの
 * 変更は対象外 (要件「進行中セッションがあれば当該セッションの変更履歴に追記」)。
 */
export function pushSettingsChange(change: SettingsChange): void {
  if (sessionLifecycle() !== "running") return;
  settingsChanges.push(change);
}

export type AccumulatorMetrics = {
  mar: number;
  isSpeaking: boolean;
  /** issue #292: detection-machine B 層の開口判定 (openSince !== null)。 */
  isOpen: boolean;
  faceDetected: boolean;
  timestamp: number;
};

/**
 * §3.12 / issue #18: lifecycle === 'running' のときだけ永続化用累積に push する。
 *
 * issue #224 で detection-loop の段構成は「段 1 = 本関数 (永続化累積) → detectionStep → 段 3 =
 * realtime HUD signal (sustainProgress) 更新」 に変わった (HUD signal が openSince に依存するため)。
 * 本関数は signal とは独立に、 ローカル変数の isSpeaking / mar を受け取り「running 中だけ DB 永続化
 * 対象として残す」 という別ドメインの責務に専念する (= UC-14 発話時間統計の累積を維持)。
 */
export function pushSessionAccumulators(metrics: AccumulatorMetrics): void {
  if (sessionLifecycle() !== "running") return;

  // §3.12: MAR 履歴は 1 秒に 1 点に間引いて永続化する。
  if (metrics.timestamp - lastMarPushAt >= MAR_INTERVAL_MS) {
    marPoints.push({
      t: metrics.timestamp,
      mar: metrics.mar,
      ...(metrics.faceDetected ? {} : { unobserved: true as const }),
    });
    lastMarPushAt = metrics.timestamp;
  }

  speakingTracker.update(metrics.isSpeaking, metrics.timestamp);
  openTracker.update(metrics.isOpen, metrics.timestamp);
  unobservedTracker.update(!metrics.faceDetected, metrics.timestamp);
}

/** 完了時 / ハイブリッド保存時に永続化用 SessionRecord を組み立てる。 */
export function buildSessionRecord(): SessionRecord | null {
  const start = startedAt();
  if (!sessionId || start === null) return null;
  const ended = endedAt() ?? Date.now();
  // issue #292: perf 基準の累積をセッション相対 ms へ正規化する (perfT0 減算)。 開いた区間の
  // クローズ位置も同じ perf 基準 (完了済みなら perfEndedAt で固定、 running 中の hybrid save なら今)。
  const closeAt = perfEndedAt ?? performance.now();
  const rel = (v: number) => v - perfT0;
  const relIntervals = (tracker: { snapshotClosed: (t: number) => Array<{ start: number; end: number }> }) =>
    tracker.snapshotClosed(closeAt).map((i) => ({ start: rel(i.start), end: rel(i.end) }));
  return {
    id: sessionId,
    startedAt: start,
    endedAt: endedAt(),
    durationMs: ended - start,
    marPoints: marPoints.map((p) => ({ ...p, t: rel(p.t) })),
    notifications: notifications().map((n) => ({ ...n, timestamp: rel(n.timestamp) })),
    speakingIntervals: relIntervals(speakingTracker),
    muteIntervals: relIntervals(muteTracker),
    openIntervals: relIntervals(openTracker),
    unobservedIntervals: relIntervals(unobservedTracker),
    settingsChanges: [...settingsChanges],
    debug: { ...debugMeta },
    persisted: false, // repository.put が true に上書きする
  };
}

export function resetSession(): void {
  setSessionLifecycle("idle");
  setStartedAt(null);
  setEndedAt(null);
  setNotifications([]);
  setLatestNotificationId(null);
  setMuted(false);
  resetRealtimeSignals();
  // §UC-24 / §3.12: 「per-session 通知抑止 Set のクリア」 は state→infra 依存禁止のため、
  // pages 層の `resetSessionAndReporting` (= `resetSession()` + `resetReportOnce()`) で
  // 並置する。 caller は本関数を直接呼ばず helper を経由する。
  sessionId = null;
  perfT0 = 0;
  perfEndedAt = null;
  marPoints.length = 0;
  speakingTracker.reset();
  openTracker.reset();
  unobservedTracker.reset();
  muteTracker.reset();
  settingsChanges.length = 0;
  lastMarPushAt = 0;
}
